import './toast.scss'

// Minimal in-repo replacement for `vue-toasted` (no Vue 3 build). Only the surface the app
// actually uses is implemented: `$toasted.show(message, options)` returning a handle whose
// `goAway(delay)` dismisses the toast. Toasts are plain DOM nodes appended to a fixed container
// on <body>; no Vue rendering is involved.

export interface ToastAction {
    text: string
    /** vue-toasted passed `(event, toastObject)`; the handle is the second arg here too. */
    onClick?: (event: MouseEvent, toast: ToastHandle) => void
}

export interface ToastOptions {
    /** ms before auto-dismiss; `0`/`null` keeps it until dismissed. Defaults to 4000. */
    duration?: number | null
    /** only `bottom-center` (default) and `top-center` are positioned; others fall back to bottom. */
    position?: string
    action?: ToastAction | ToastAction[]
    /** replace any toast already shown in the same position. */
    singleton?: boolean
    /** stretch the toast to the container width (vue-toasted's `fullWidth` / `fitToScreen`). */
    fullWidth?: boolean
    fitToScreen?: boolean
    /** extra CSS class(es) to add to the toast element. */
    className?: string | string[]
}

export interface ToastHandle {
    el: HTMLElement
    goAway(delay?: number): void
}

const POSITIONS = ['bottom-center', 'top-center']

function getContainer(position: string): HTMLElement {
    const id = `s-toast-container-${position}`
    let container = document.getElementById(id)
    if (!container) {
        container = document.createElement('div')
        container.id = id
        container.className = `s-toast-container s-toast-container--${position}`
        document.body.appendChild(container)
    }
    return container
}

export const toast = {
    show(message: string, options: ToastOptions = {}): ToastHandle {
        const position = POSITIONS.indexOf(options.position || '') >= 0 ? options.position! : 'bottom-center'
        const container = getContainer(position)

        if (options.singleton) {
            Array.from(container.querySelectorAll('.s-toast')).forEach(el => el.remove())
        }

        const el = document.createElement('div')
        el.className = 's-toast'
        if (options.fullWidth || options.fitToScreen) {
            el.classList.add('s-toast--full-width')
        }
        if (options.className) {
            ([] as string[]).concat(options.className).forEach(c => c && el.classList.add(c))
        }

        const text = document.createElement('span')
        text.className = 's-toast__message'
        text.textContent = message
        el.appendChild(text)

        let timeoutId: ReturnType<typeof setTimeout> | undefined

        const handle: ToastHandle = {
            el,
            goAway(delay = 0) {
                if (timeoutId) {
                    clearTimeout(timeoutId)
                    timeoutId = undefined
                }
                setTimeout(() => el.remove(), delay)
            },
        }

        const actions = options.action
            ? (Array.isArray(options.action) ? options.action : [options.action])
            : []
        for (const action of actions) {
            const button = document.createElement('button')
            button.type = 'button'
            button.className = 's-toast__action'
            button.textContent = action.text
            button.addEventListener('click', event => {
                if (action.onClick) {
                    action.onClick(event, handle)
                } else {
                    handle.goAway(0)
                }
            })
            el.appendChild(button)
        }

        container.appendChild(el)

        const duration = options.duration === undefined ? 4000 : options.duration
        if (duration) {
            timeoutId = setTimeout(() => handle.goAway(0), duration)
        }

        return handle
    },
}

export type Toast = typeof toast

export const ToastPlugin = {
    install(vueOrApp: any) {
        // Vue 2-style plugin. Under @vue/compat MODE 2 (GLOBAL_PROTOTYPE) each app's
        // configure-vue.ts still calls `Vue.use()` with the compat Vue, whose `.prototype` backs
        // every component instance — same wiring as the other `Vue.prototype.$x` plugins there.
        // The `globalProperties` branch keeps it working through a real `app.use()` as well.
        if (vueOrApp && vueOrApp.prototype) {
            vueOrApp.prototype.$toasted = toast
        } else if (vueOrApp && vueOrApp.config && vueOrApp.config.globalProperties) {
            vueOrApp.config.globalProperties.$toasted = toast
        }
    },
}

export default ToastPlugin
